import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Badge, Tooltip } from 'antd';
import { CustomerServiceOutlined } from '@ant-design/icons';
import CreateTicketModal from './CreateTicketModal';
import ticketService from '../services/ticketService';

/**
 * Ticket button - lets users create tickets; approvers go to the ticket review page.
 * `floating` is the round button pinned to the corner; `nav` is a sidebar item.
 */
const TicketButton = ({ userEmail, isAdmin = false, variant = 'floating', collapsed = false }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);

  // Fetch open ticket count
  const fetchPendingCount = async () => {
    try {
      const filters = { status: 'open' };
      // For regular users, only count their own open tickets
      if (!isAdmin) {
        filters.email = userEmail;
      }
      
      const response = await ticketService.listTickets(filters);
      
      if (response.success) {
      }
    } catch (error) {
      // ignore
    }
  };

  useEffect(() => {
    if (userEmail) {
      fetchPendingCount();
      // Refresh count every 30 seconds
      const interval = setInterval(fetchPendingCount, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, isAdmin]);

  const handleClick = () => {
    if (isAdmin) {
      navigate('/support/tickets');
    } else {
      setShowCreateModal(true);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    fetchPendingCount(); // Refresh count after closing
  };


  const navLabel = isAdmin ? 'Manage tickets' : 'Support';
  const navItem = (
    <button
      type="button"
      className={`app-nav__item${isAdmin && location.pathname.startsWith('/support') ? ' is-active' : ''}`}
      onClick={handleClick}
      aria-label={collapsed ? navLabel : undefined}
      aria-current={isAdmin && location.pathname.startsWith('/support') ? 'page' : undefined}
    >
      <span className="app-nav__icon">
        <CustomerServiceOutlined />
      </span>
      <span className="app-nav__label">{navLabel}</span>
    </button>
  );

  return (
    <>
      {variant === 'nav' ? (
        collapsed ? <Tooltip title={navLabel} placement="right">{navItem}</Tooltip> : navItem
      ) : (
      <Tooltip title={isAdmin ? "Manage Tickets" : "Create Support Ticket"} placement="left">
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 1000 }}>
          <Badge 
            count={pendingCount}
            showZero={false}
            offset={[-10, 10]}
            style={{ 
              backgroundColor: '#ff4d4f',
              boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)'
            }}
          >
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<CustomerServiceOutlined style={{ fontSize: '24px' }} />}
              onClick={handleClick}
              style={{
                width: '64px',
                height: '64px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Badge>
        </div>
      </Tooltip>
      )}

      {/* Create Ticket Modal */}
      <CreateTicketModal
        visible={showCreateModal}
        onClose={handleCloseCreateModal}
        userEmail={userEmail}
      />

    </>
  );
};

export default TicketButton;
